import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import api, { apiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { initials, relativeTime } from "@/lib/format";
import {
  Instagram, Facebook, Linkedin, ArrowRight, ChevronDown, ChevronRight, RefreshCw, CheckCircle2,
  AlertTriangle, HelpCircle, Loader2, Radar, Copy, ExternalLink, Pencil, Circle, Sparkles, Lock,
} from "lucide-react";

/*
 * v8 · «Mi perfil en redes».
 * Centrado en el diagnóstico del perfil del usuario: lo que verá la gente cuando le escribas.
 * El letrero es genérico (habrá más redes); dentro, de momento solo Instagram está activo.
 */

const NETWORKS = [
  { key: "instagram", label: "Instagram", icon: Instagram, active: true },
  { key: "facebook", label: "Facebook", icon: Facebook, active: false },
  { key: "linkedin", label: "LinkedIn", icon: Linkedin, active: false },
];

const LEVEL_TONE = {
  listo: { box: "border-brand-pos/40 bg-brand-pos/[0.06]", text: "text-brand-pos", icon: CheckCircle2 },
  mejorable: { box: "border-brand-accent/40 bg-brand-accent/[0.06]", text: "text-brand-accent", icon: Sparkles },
  arregla: { box: "border-brand-neg/40 bg-brand-neg/[0.06]", text: "text-brand-neg", icon: AlertTriangle },
};

const LIGHT = { green: "bg-brand-pos", amber: "bg-brand-accent", red: "bg-brand-neg" };

const STATUS = {
  ok: { icon: CheckCircle2, tone: "text-brand-pos", label: "Bien" },
  mejorable: { icon: AlertTriangle, tone: "text-brand-neg", label: "Mejorar" },
  sin_comprobar: { icon: HelpCircle, tone: "text-brand-muted", label: "Sin comprobar" },
  pendiente: { icon: HelpCircle, tone: "text-brand-muted", label: "Te preguntamos abajo" },
};

const ANSWERS = [["si", "Sí"], ["no", "No"], ["no_lo_se", "No lo sé"]];

const fmtNum = (n) => (typeof n === "number" ? n.toLocaleString("es-ES") : "—");

export default function Channels() {
  const [link, setLink] = useState(null);
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [input, setInput] = useState("");
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState({});

  const load = async () => {
    try {
      const [l, r] = await Promise.all([
        api.get("/instagram/link").then((x) => x.data).catch(() => ({ linked: false })),
        api.get("/audit-runs").then((x) => x.data || {}).catch(() => ({})),
      ]);
      setLink(l);
      setRun(l?.linked ? r.instagram || null : null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const analyze = async () => {
    const v = input.trim();
    if (!v) { toast.error("Escribe tu usuario o enlace de Instagram."); return; }
    setBusy("link");
    try {
      const r = await api.post("/instagram/link", { profile: v });
      toast.success(`Perfil @${r.data.username} analizado.`);
      setInput(""); setEditing(false);
      setRun(r.data.run);
      setLink((p) => ({ ...(p || {}), linked: true, username: r.data.username }));
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const refresh = async () => {
    setBusy("refresh");
    try {
      const r = await api.post("/instagram/profile-audit/refresh");
      setRun(r.data.run);
      toast.success("Diagnóstico actualizado.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const toggleTask = async (id, done) => {
    try {
      const r = await api.post("/instagram/profile-audit/task", { id, done });
      if (r.data.run) setRun(r.data.run);
    } catch (e) { toast.error(apiError(e)); }
  };

  const answer = async (id, ans) => {
    try {
      const r = await api.post("/instagram/profile-audit/confirm", { id, answer: ans });
      if (r.data.run) setRun(r.data.run);
    } catch (e) { toast.error(apiError(e)); }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text).then(() => toast.success("Copiado.")).catch(() => toast.error("No se ha podido copiar."));
  };

  const linked = !!link?.linked;
  const isV8 = run?.version === 2;
  const snap = run?.public_snapshot || {};
  const tone = LEVEL_TONE[run?.level_key] || LEVEL_TONE.mejorable;
  const LevelIcon = tone.icon;
  const pic = typeof snap.profilePicUrl === "string" && snap.profilePicUrl.startsWith("http") ? snap.profilePicUrl : "";
  const username = run?.username || link?.username || "";
  const confirmations = run?.confirmations || {};

  return (
    <div className="space-y-6">
      <PageHeader
        title="Mi perfil en redes"
        subtitle="Cuando escribes a alguien, lo primero que hace es mirar tu perfil. Aquí ves si está preparado y qué arreglar."
        testId="channels-header"
      />

      {/* Redes: hoy solo Instagram */}
      <div className="flex flex-wrap items-center gap-2" data-testid="profile-networks">
        {NETWORKS.map((n) => {
          const Icon = n.icon;
          return (
            <span
              key={n.key}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${
                n.active ? "border-brand-accent/50 bg-brand-accent/[0.08] text-brand-ink" : "border-brand-line text-brand-muted"
              }`}
            >
              <Icon size={13} /> {n.label}{!n.active && <span className="font-normal"> · próximamente</span>}
            </span>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-brand-muted"><Loader2 size={16} className="animate-spin" /> Cargando…</div>
      ) : !linked || editing ? (
        /* Sin perfil vinculado (o cambiándolo) */
        <section className="rounded-lg border border-brand-line bg-brand-panel p-6" data-testid="profile-link-form">
          <h2 className="text-base font-bold text-brand-ink">{editing ? "Cambiar el perfil analizado" : "Analiza tu perfil de Instagram"}</h2>
          <p className="mt-1 text-sm text-brand-muted">
            Escribe tu usuario o pega el enlace. Leemos solo lo que es público y te decimos qué ve un cliente en 10 segundos.
          </p>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && analyze()}
              placeholder="@tuusuario o https://instagram.com/tuusuario"
              className="h-10 flex-1 rounded-md border border-brand-line bg-transparent px-3 text-sm text-brand-ink outline-none focus:border-brand-accent"
              data-testid="profile-link-input"
            />
            <button
              onClick={analyze}
              disabled={busy === "link"}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-brand-accent px-4 text-sm font-bold text-brand-canvas hover:brightness-110 disabled:opacity-60"
              data-testid="profile-link-submit"
            >
              {busy === "link" ? <><Loader2 size={15} className="animate-spin" /> Analizando (≈1 min)…</> : <>Analizar mi perfil <ArrowRight size={15} /></>}
            </button>
            {editing && (
              <button onClick={() => setEditing(false)} className="h-10 rounded-md border border-brand-line px-4 text-sm text-brand-muted">Cancelar</button>
            )}
          </div>
        </section>
      ) : (
        <>
          {/* Cabecera del perfil */}
          <section className="flex flex-col gap-4 rounded-lg border border-brand-line bg-brand-panel p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="profile-card">
            <div className="flex items-center gap-4">
              {pic ? (
                <img src={pic} alt="" referrerPolicy="no-referrer" className="h-14 w-14 rounded-full object-cover" />
              ) : (
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-accent/[0.12] text-lg font-bold text-brand-accent">
                  {initials(snap.fullName || username)}
                </span>
              )}
              <div>
                <p className="flex items-center gap-1.5 text-base font-bold text-brand-ink">
                  <Instagram size={15} /> @{username}
                  {snap.private && <span className="inline-flex items-center gap-1 text-xs font-normal text-brand-neg"><Lock size={12} /> privado</span>}
                </p>
                {snap.fullName && <p className="text-sm text-brand-muted">{snap.fullName}</p>}
                <p className="mt-0.5 text-xs text-brand-muted">
                  {fmtNum(snap.followersCount)} seguidores · {fmtNum(snap.postsCount)} publicaciones
                  {run?.checked_at && <> · revisado {relativeTime(run.checked_at)}</>}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <a href={`https://www.instagram.com/${username}/`} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1.5 rounded-md border border-brand-line px-3 py-2 text-xs text-brand-ink hover:border-brand-accent">
                <ExternalLink size={13} /> Ver en Instagram
              </a>
              <button onClick={() => { setEditing(true); setInput(username); }}
                      className="inline-flex items-center gap-1.5 rounded-md border border-brand-line px-3 py-2 text-xs text-brand-ink hover:border-brand-accent"
                      data-testid="profile-change">
                <Pencil size={13} /> Cambiar perfil
              </button>
              <button onClick={refresh} disabled={busy === "refresh"}
                      className="inline-flex items-center gap-1.5 rounded-md border border-brand-accent/60 px-3 py-2 text-xs font-semibold text-brand-accent disabled:opacity-60"
                      data-testid="profile-refresh">
                {busy === "refresh" ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Actualizar diagnóstico
              </button>
            </div>
          </section>

          {!run || !isV8 ? (
            <section className="rounded-lg border border-dashed border-brand-line bg-brand-panel p-6 text-sm text-brand-muted" data-testid="profile-outdated">
              Hemos mejorado el diagnóstico. Pulsa <b className="text-brand-ink">Actualizar diagnóstico</b> para ver el nuevo (tarda alrededor de un minuto).
            </section>
          ) : (
            <>
              {/* Veredicto */}
              <section className={`rounded-lg border p-5 ${tone.box}`} data-testid="profile-verdict">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <LevelIcon size={22} className={`mt-0.5 shrink-0 ${tone.text}`} />
                    <div>
                      <p className={`text-lg font-bold ${tone.text}`} data-testid="profile-level">{run.level}</p>
                      <p className="mt-1 text-sm text-brand-ink">{run.summary}</p>
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-2xl font-bold text-brand-ink" data-testid="profile-score">{run.score}<span className="text-sm text-brand-muted">/100</span></p>
                  </div>
                </div>
                {!run.ai_used && (
                  <p className="mt-3 text-xs text-brand-muted" data-testid="profile-no-ai">
                    Esta vez solo hemos podido aplicar las comprobaciones automáticas; la lectura de tu bio y tus publicaciones no estaba disponible. Vuelve a actualizar en un rato.
                  </p>
                )}
              </section>

              {/* Arregla esto primero */}
              {run.tasks?.length > 0 && (
                <section className="rounded-lg border border-brand-line bg-brand-panel p-5" data-testid="profile-tasks">
                  <h2 className="text-base font-bold text-brand-ink">Arregla esto primero</h2>
                  <p className="mt-0.5 text-xs text-brand-muted">Lo que más te va a ayudar, en orden. Márcalo cuando lo hayas hecho.</p>
                  <ol className="mt-4 space-y-3">
                    {run.tasks.map((t, i) => (
                      <li key={t.id} className={`rounded-md border border-brand-line p-4 ${t.done ? "opacity-60" : ""}`} data-testid={`profile-task-${t.id}`}>
                        <div className="flex items-start gap-3">
                          <button onClick={() => toggleTask(t.id, !t.done)} className="mt-0.5 shrink-0" aria-label={t.done ? "Marcar como pendiente" : "Marcar como hecho"}>
                            {t.done ? <CheckCircle2 size={18} className="text-brand-pos" /> : <Circle size={18} className="text-brand-muted" />}
                          </button>
                          <div className="min-w-0 flex-1">
                            <p className={`text-sm font-bold text-brand-ink ${t.done ? "line-through" : ""}`}>{i + 1}. {t.title}</p>
                            {t.why && <p className="mt-1 text-xs text-brand-muted">{t.why}</p>}
                            {t.fix && <p className="mt-2 text-sm text-brand-ink"><span className="font-semibold">Qué hacer:</span> {t.fix}</p>}
                            {t.suggestion && (
                              <div className="mt-3 rounded-md border border-brand-accent/40 bg-brand-accent/[0.05] p-3">
                                <p className="text-xs font-semibold text-brand-accent">Propuesta de bio</p>
                                <p className="mt-1 whitespace-pre-line text-sm text-brand-ink">{t.suggestion}</p>
                                <button onClick={() => copy(t.suggestion)} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-brand-accent">
                                  <Copy size={12} /> Copiar
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ol>
                </section>
              )}

              {/* Lo que no podemos ver */}
              {run.confirm?.length > 0 && (
                <section className="rounded-lg border border-brand-line bg-brand-panel p-5" data-testid="profile-confirm">
                  <h2 className="text-base font-bold text-brand-ink">Esto no lo podemos ver desde fuera</h2>
                  <p className="mt-0.5 text-xs text-brand-muted">Respóndenos y lo tenemos en cuenta en la puntuación.</p>
                  <ul className="mt-3 divide-y divide-brand-line">
                    {run.confirm.map((c) => (
                      <li key={c.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm text-brand-ink">{c.title}</p>
                          {c.why && <p className="text-xs text-brand-muted">{c.why}</p>}
                        </div>
                        <div className="flex shrink-0 gap-1.5">
                          {ANSWERS.map(([k, label]) => (
                            <button
                              key={k}
                              onClick={() => answer(c.id, k)}
                              className={`rounded-md border px-3 py-1 text-xs ${
                                confirmations[c.id] === k ? "border-brand-accent bg-brand-accent/[0.12] font-semibold text-brand-ink" : "border-brand-line text-brand-muted hover:border-brand-accent"
                              }`}
                              data-testid={`confirm-${c.id}-${k}`}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Detalle por áreas */}
              <section className="rounded-lg border border-brand-line bg-brand-panel p-5" data-testid="profile-areas">
                <h2 className="text-base font-bold text-brand-ink">Revisión completa</h2>
                <p className="mt-0.5 text-xs text-brand-muted">
                  {run.counts?.ok ?? 0} de {run.counts?.total ?? 0} puntos bien. Abre cada apartado para ver qué hemos mirado y por qué.
                </p>
                <div className="mt-3 space-y-2">
                  {(run.areas || []).map((a) => {
                    const isOpen = !!open[a.key];
                    return (
                      <div key={a.key} className="rounded-md border border-brand-line">
                        <button
                          onClick={() => setOpen((o) => ({ ...o, [a.key]: !o[a.key] }))}
                          className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
                          data-testid={`profile-area-${a.key}`}
                        >
                          <span className="flex items-center gap-2.5">
                            <span className={`h-2.5 w-2.5 rounded-full ${LIGHT[a.light] || "bg-brand-muted"}`} />
                            <span className="text-sm font-semibold text-brand-ink">{a.title}</span>
                          </span>
                          <span className="flex items-center gap-2 text-xs text-brand-muted">
                            {a.ok}/{a.total} {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </span>
                        </button>
                        {isOpen && (
                          <ul className="divide-y divide-brand-line border-t border-brand-line">
                            {a.items.map((it) => {
                              const st = STATUS[it.status] || STATUS.sin_comprobar;
                              const Icon = st.icon;
                              return (
                                <li key={it.id} className="flex gap-3 px-4 py-3">
                                  <Icon size={16} className={`mt-0.5 shrink-0 ${st.tone}`} />
                                  <div className="min-w-0">
                                    <p className="text-sm text-brand-ink">
                                      {it.title} <span className={`text-xs ${st.tone}`}>· {st.label}</span>
                                    </p>
                                    {it.evidence && <p className="mt-1 text-xs italic text-brand-muted">«{it.evidence}»</p>}
                                    {it.reason && <p className="mt-1 text-xs text-brand-muted">{it.reason}</p>}
                                    {it.status === "mejorable" && it.fix && <p className="mt-1 text-xs text-brand-ink"><span className="font-semibold">Qué hacer:</span> {it.fix}</p>}
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            </>
          )}

          <Link to="/app/oportunidades" data-testid="channels-to-opportunities"
                className="flex items-center justify-between gap-3 rounded-lg border border-brand-line bg-brand-panel p-4 transition-colors hover:border-brand-accent">
            <span className="flex items-center gap-3">
              <Radar size={18} className="text-brand-accent" />
              <span className="text-sm text-brand-ink">Cuando lo tengas listo, busca a quién escribir</span>
            </span>
            <ArrowRight size={16} className="text-brand-accent" />
          </Link>
        </>
      )}

      <p className="text-xs text-brand-muted" data-testid="profile-footnote">
        Solo leemos la información pública de tu perfil. Más adelante podrás conectar tu cuenta oficialmente para ver también estadísticas y mensajes.
      </p>
    </div>
  );
}
